import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  X,
  Minimize2,
  Send,
  ChevronDown,
  FileSearch,
  PenTool,
  Handshake,
  MessageCircle,
  Sparkles,
  Info,
  Lock,
  Compass,
} from 'lucide-react';
import { useAI } from '@/hooks/useAI';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { TIER_LEVELS, TIER_LABELS } from '@/lib/constants';
import type { AIToolType, SubscriptionTier } from '@/types/common';
import styles from './layout.module.css';

interface AIToolOption {
  value: AIToolType;
  label: string;
  icon: React.ReactNode;
  description: string;
  // Minimum subscription tier required to USE this tool. Mirrors the backend
  // source of truth, TOOL_TYPE_MIN_TIER in server/src/routes/ai.ts. Keep these
  // in sync: a value here that is looser than the backend lets the FE submit a
  // request the backend will reject with 403; tighter than the backend hides a
  // tool the user could actually use. Typed as SubscriptionTier (not a free
  // display string) so it participates in the TIER_LEVELS ordering used for
  // gating and so a typo fails typecheck.
  minTier?: SubscriptionTier;
}

const AI_TOOLS: AIToolOption[] = [
  { value: 'general_chat', label: 'AI Manager', icon: <MessageCircle size={14} />, description: 'General business advice', minTier: 'essential' },
  { value: 'deal_review', label: 'Deal Reviewer', icon: <Handshake size={14} />, description: 'Analyze deal terms & red flags', minTier: 'pro' },
  { value: 'contract_analysis', label: 'Contract Analyzer', icon: <FileSearch size={14} />, description: 'Review contract language', minTier: 'pro' },
  { value: 'pitch_writer', label: 'Pitch Writer', icon: <PenTool size={14} />, description: 'Write brand outreach pitches', minTier: 'manager' },
  { value: 'rate_advisor', label: 'Rate Advisor', icon: <Sparkles size={14} />, description: 'Get rate recommendations', minTier: 'pro' },
  { value: 'career_strategy', label: 'Career Strategist', icon: <Compass size={14} />, description: 'Long-term growth & positioning', minTier: 'pro' },
  { value: 'negotiation', label: 'Negotiation Coach', icon: <Bot size={14} />, description: 'Coaching for negotiations', minTier: 'pro' },
];

const SUGGESTED_PROMPTS: Record<AIToolType, string[]> = {
  general_chat: ['Review my current deal pipeline', 'What should I prioritize this week?', 'Help me set better rates'],
  deal_review: ['Flag any red flags in this deal', 'Is this rate fair for my audience size?', 'What usage rights should I negotiate?'],
  contract_analysis: ['Check this contract for unfair clauses', 'Explain the exclusivity terms', 'What are my key obligations?'],
  pitch_writer: ['Write a cold pitch for a beauty brand', 'Create a follow-up email template', 'Help me position my value prop'],
  rate_advisor: ['What should I charge for a TikTok video?', 'How do I price usage rights?', 'Am I undercharging for my niche?'],
  career_strategy: ['How do I break into luxury brands?', 'Should I expand to YouTube?', 'How do I build long-term partnerships?'],
  negotiation: ['They countered below my rate', 'How do I ask for more without losing the deal?', 'Script for a negotiation call'],
};

function hasToolAccess(tool: AIToolOption, currentTier: SubscriptionTier): boolean {
  if (!tool.minTier) return true;
  return TIER_LEVELS[currentTier] >= TIER_LEVELS[tool.minTier];
}

const WelcomeMessage: React.FC<{ tool: AIToolOption }> = ({ tool }) => (
  <div className={styles.aiMsgRow}>
    <div className={styles.aiAvatar}><Bot size={14} /></div>
    <div className={styles.aiMsgBubble + ' ' + styles.aiMsgAssistant}>
      <p>
        Hey! I'm your <span style={{ color: '#7B5CFA', fontWeight: 500 }}>{tool.label}</span>. {tool.description}.
        What can I help you with?
      </p>
    </div>
  </div>
);

const UpgradeGate: React.FC<{ tool: AIToolOption; onUpgrade: () => void }> = ({ tool, onUpgrade }) => {
  const tierLabel = tool.minTier ? TIER_LABELS[tool.minTier] : 'a higher';
  return (
    <div
      className={styles.aiMessages}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 14, padding: '32px 24px' }}
    >
      <div
        style={{ width: 52, height: 52, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(123, 92, 250, 0.12)', color: '#7B5CFA' }}
      >
        <Lock size={24} />
      </div>
      <p style={{ fontWeight: 600, color: '#E5E7EB', fontSize: 15 }}>{tool.label}</p>
      <p style={{ fontSize: 13, color: '#8B92A8', maxWidth: 280, lineHeight: 1.5 }}>
        {tool.description}. This tool requires the{' '}
        <span style={{ color: '#7B5CFA', fontWeight: 600 }}>{tierLabel}</span> plan or higher.
      </p>
      <button
        onClick={onUpgrade}
        style={{
          marginTop: 4,
          padding: '10px 20px',
          borderRadius: 10,
          border: 'none',
          background: '#7B5CFA',
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Sparkles size={15} />
        Upgrade to {tierLabel}
      </button>
    </div>
  );
};

export const AIPanel: React.FC = () => {
  const { aiPanelOpen, setAIPanelOpen, activeAITool, setActiveAITool, aiContext } = useUIStore();
  const { profile } = useAuthStore();
  const navigate = useNavigate();

  const { messages, isLoading, sendMessage, clearConversation } = useAI(activeAITool);
  const [input, setInput] = useState('');
  const [toolDropdownOpen, setToolDropdownOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const currentTier: SubscriptionTier = profile?.subscription_tier ?? 'starter';
  const activeTool = AI_TOOLS.find((t) => t.value === activeAITool) ?? AI_TOOLS[0]!;
  const toolAccessible = hasToolAccess(activeTool, currentTier);
  const suggestedPrompts = SUGGESTED_PROMPTS[activeAITool] ?? SUGGESTED_PROMPTS.general_chat;

  useEffect(() => {
    if (aiPanelOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [aiPanelOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || isLoading) return;
    setInput('');
    await sendMessage(content, aiContext ?? undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleToolSelect = (tool: AIToolOption) => {
    setActiveAITool(tool.value);
    setToolDropdownOpen(false);
    clearConversation();
  };

  const handleUpgrade = () => {
    setAIPanelOpen(false);
    navigate('/settings');
  };

  if (!aiPanelOpen) return null;

  return (
    <>
      <div className={styles.aiBackdrop} onClick={() => setAIPanelOpen(false)} aria-hidden="true" />

      <aside className={styles.aiPanel} aria-label="AI Manager panel">
        {/* Header */}
        <div className={styles.aiHeader}>
          <div className={styles.aiHeaderIcon}><Bot size={14} /></div>
          <span className={styles.aiHeaderTitle}>AI Manager</span>
          <button onClick={() => setAIPanelOpen(false)} className={styles.aiCloseBtn} aria-label="Close AI panel">
            <span className={styles.desktopOnly}><Minimize2 size={14} /></span>
            <span className={styles.mobileOnly}><X size={16} /></span>
          </button>
        </div>

        {/* Tool selector */}
        <div className={styles.aiToolSelector}>
          <button
            onClick={() => setToolDropdownOpen((o) => !o)}
            className={styles.aiToolBtn}
            aria-haspopup="listbox"
            aria-expanded={toolDropdownOpen}
          >
            <span className={styles.aiToolBtnIcon}>{activeTool.icon}</span>
            <span className={styles.aiToolBtnLabel}>{activeTool.label}</span>
            <ChevronDown
              size={14}
              className={`${styles.aiToolBtnChevron} ${toolDropdownOpen ? styles.aiToolBtnChevronOpen : ''}`}
            />
          </button>

          {toolDropdownOpen && (
            <>
              <div className={styles.aiToolDropdownBackdrop} onClick={() => setToolDropdownOpen(false)} aria-hidden="true" />
              <div className={styles.aiToolDropdown} role="listbox" aria-label="Select AI tool">
                {AI_TOOLS.map((tool) => (
                  <button
                    key={tool.value}
                    role="option"
                    aria-selected={tool.value === activeAITool}
                    onClick={() => handleToolSelect(tool)}
                    className={`${styles.aiToolOption} ${tool.value === activeAITool ? styles.aiToolOptionActive : ''}`}
                  >
                    <span className={tool.value === activeAITool ? styles.aiToolOptionIconActive : styles.aiToolOptionIcon}>
                      {tool.icon}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className={tool.value === activeAITool ? styles.aiToolOptionNameActive : styles.aiToolOptionName}>
                        {tool.label}
                      </p>
                      <p className={styles.aiToolOptionDesc}>{tool.description}</p>
                    </div>
                    {tool.minTier && <span className={styles.aiToolOptionBadge}>{TIER_LABELS[tool.minTier]}</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Context indicator */}
        {aiContext?.page && (
          <div className={styles.aiContext}>
            <Info size={12} style={{ color: '#7B5CFA', flexShrink: 0 }} />
            <p className={styles.aiContextText}>
              Context: <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{aiContext.page}</span>
              {aiContext.resourceId && ` · ID ${aiContext.resourceId.slice(0, 8)}…`}
            </p>
          </div>
        )}

        {!toolAccessible ? (
          /* Tier gate — the active tool is above the user's plan. Block all
             input (compose + send) and surface an upgrade path instead of
             letting the user fire a request the backend will 403. Gating on
             the ACTIVE tool (not just the selection handler) also covers the
             panel being opened with a pre-set activeAITool from elsewhere. */
          <UpgradeGate tool={activeTool} onUpgrade={handleUpgrade} />
        ) : (
          <>
            {/* Messages */}
            <div className={styles.aiMessages}>
              {messages.length === 0 && <WelcomeMessage tool={activeTool} />}

              {messages.map((msg, i) => (
                <div key={i} className={`${styles.aiMsgRow} ${msg.role === 'user' ? styles.aiMsgRowUser : ''}`}>
                  {msg.role === 'assistant' && (
                    <div className={styles.aiAvatar}><Bot size={12} /></div>
                  )}
                  <div className={`${styles.aiMsgBubble} ${msg.role === 'assistant' ? styles.aiMsgAssistant : styles.aiMsgUser}`}>
                    {msg.content}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className={styles.aiMsgRow}>
                  <div className={styles.aiAvatar}><Bot size={12} /></div>
                  <div className={styles.aiMsgBubble + ' ' + styles.aiMsgAssistant}>
                    <div className={styles.aiLoadingDots}>
                      <div className={styles.aiLoadingDot} />
                      <div className={styles.aiLoadingDot} />
                      <div className={styles.aiLoadingDot} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Suggested prompts */}
            {messages.length === 0 && (
              <div className={styles.aiSuggested}>
                <p className={styles.aiSuggestedLabel}>Suggested</p>
                {suggestedPrompts.slice(0, 3).map((prompt) => (
                  <button key={prompt} onClick={() => handleSend(prompt)} className={styles.aiSuggestedBtn}>
                    {prompt}
                  </button>
                ))}
              </div>
            )}

            {/* Input area */}
            <div className={styles.aiInputArea}>
              <div className={styles.aiInputWrap}>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Ask ${activeTool.label}…`}
                  rows={1}
                  className={styles.aiInputField}
                  style={{ fieldSizing: 'content' } as React.CSSProperties}
                  disabled={isLoading}
                  aria-label="Message input"
                />
                <button
                  onClick={() => handleSend()}
                  disabled={!input.trim() || isLoading}
                  className={styles.aiSendBtn}
                  aria-label="Send message"
                >
                  <Send size={14} />
                </button>
              </div>

              {profile?.subscription_status !== 'active' && (
                <p className={styles.aiUpgradeHint}>Upgrade for AI access</p>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
};
